const DATE_REG_EXP = /^(\d{1,2})([/-])(\d{1,2})([/-])(\d{4})\b/;

/**
 * Converts string values to arrays by parsing them as JSON. If the JSON parse
 * fails, or doesn't result in an array, an empty array is returned. Any other
 * passed in value that is not an array will coerce to an empty array.
 */
export function coerceToArray(el: HTMLElement, prop: string, val: any): any[] {
  if (typeof val === 'string') {
    try {
      val = JSON.parse(val);
    } catch (e) {
      console.error(
        `${el.tagName} property "${prop}" expects valid JSON if passed a string`,
        e
      );
      return [];
    }
  }

  if (!Array.isArray(val)) {
    console.error(
      `${
        el.tagName
      } property "${prop}" expected an array but got type "${typeof val}"`
    );
    return [];
  }

  return val;
}

/**
 * Convert any value to a boolean. The string literal "false" converts to
 * `false`. All other string values coerce to `true`. For other types, use the
 * truthiness of the value.
 * @param val Any value to coerce.
 * @return A boolean value.
 */
export function coerceToBoolean(val: any): boolean {
  if (val === 'false') {
    return false;
  }

  if (val === '') {
    return true;
  }

  return Boolean(val);
}

export function coerceToDate(value) {
  if (value instanceof Date) return value;

  // 11/20/2000
  const parts = DATE_REG_EXP.exec(value);
  if (parts && parts[2] === parts[4]) {
    return new Date(+parts[5], +parts[1] - 1, +parts[3]);
  }

  return new Date();
}

/**
 * A converter object that can be used to reflect booleans in a lit-element
 * component. Use as the value of the `converter` field of a property.
 */
export const booleanConverter = {
  fromAttribute(val) {
    return coerceToBoolean(val);
  },
  toAttribute(val) {
    val = coerceToBoolean(val);
    if (!val) {
      return null;
    }
    return '';
  },
};

/**
 * A converter object that can be used to reflect (date | string) in a lit-element
 * component. Use as the value of the `converter` field of a property.
 */
export const dateToStringConverter = {
  fromAttribute(val) {
    return val;
  },
  toAttribute(val) {
    if (val instanceof Date) {
      return val.toISOString();
    }
    return val;
  },
};

/**
 * Converts all of an element's properties leveraging 'booleanConverter' to coerce string 'true'/'false' properties to their respective boolean values. Added to prevent regression.
 */
export const coerceBooleanClassProperties = element => {
  const { properties } = element;
  for (const key in properties) {
    const prop = properties[key];
    if (prop.converter === booleanConverter) {
      const privateKey = `_${key}`;
      Object.defineProperty(element.prototype, key, {
        get() {
          return this[privateKey];
        },
        set(val) {
          val = coerceToBoolean(val);
          const oldVal = this[key];
          this[privateKey] = val;
          this.requestUpdate(key, oldVal);
        },
      });
    }
  }
};
